import { useEffect, useState } from 'react';
import { ETemplateStyles, ESnapshotExists, EOrganization } from 'lib/types';
import MadlibEditor, {
  MadlibNoticeHandlebarData
} from 'routes/madlib/components/MadlibEditor';
import QuestionForm from 'routes/madlib/components/QuestionForm';
import { MadlibDataType } from 'lib/types/madlib';
import { Alert } from 'lib/components/Alert';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import LoadingState from 'components/LoadingState';
import { NoticeContentFields } from 'routes/placeScroll/NoticeContentStep/NoticeContentFields';
import { MadlibsConfig } from '../helpers/config';

type NoticeEditorMadlibProps = {
  newspaper?: ESnapshotExists<EOrganization>;
  setIsMadlibComplete: (complete: boolean) => void;
  templateStyles?: ETemplateStyles;
  madlibConfig: MadlibsConfig | null;
  madlibData: MadlibDataType;
  onEditorUpdate: (renderedHtml: string) => void;
  onTemplateDataChange: (madlibData: MadlibDataType) => void;
  noticeHandlebarData: MadlibNoticeHandlebarData;
};

function NoticeEditorMadlib({
  newspaper,
  setIsMadlibComplete,
  templateStyles,
  madlibConfig,
  madlibData,
  onEditorUpdate,
  onTemplateDataChange,
  noticeHandlebarData
}: NoticeEditorMadlibProps) {
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [renderedHtml, setRenderedHtml] = useState('');
  const [isAllNonEmptyAnswers, setIsAllNonEmptyAnswers] = useState(false);
  const [validationResults, setValidationResults] = useState<
    Record<string, boolean>
  >({});
  const [madlibEditorInitialized, setMadlibEditorInitialized] = useState(false);

  /**
   * Determines whether or not the madlib questions inside of the template data are sufficient
   * This should only run _after_ the madlib editor has been initialized
   */
  useEffect(() => {
    if (!madlibConfig) {
      return;
    }
    if (madlibConfig?.questionConfig?.showFullContentEditor) {
      // If the full content editor is shown, we don't need to check for madlib completion
      setIsMadlibComplete(isAllNonEmptyAnswers);
      return;
    }
    onEditorUpdate(renderedHtml);
    if (madlibData.templateData && madlibEditorInitialized) {
      const noInvalidData = Object.entries(madlibData.templateData).every(
        ([key, input]) => !!input && validationResults[key] !== false
      );
      setIsMadlibComplete(noInvalidData && !!isAllNonEmptyAnswers);
    }
  }, [!!madlibConfig, madlibData, validationResults, madlibEditorInitialized]);

  if (!madlibConfig) {
    return (
      <LoadingState
        timeout={600}
        context={{
          location: 'NoticeEditorMadlib',
          tags: { newspaperId: newspaper?.id || '' }
        }}
      />
    );
  }

  const {
    template: rawTemplate,
    questions,
    questionConfig
  } = madlibConfig || {};

  return (
    <>
      <QuestionForm
        questions={questions}
        questionsConfig={questionConfig}
        questionTemplateData={madlibData.questionTemplateData}
        onChange={form => {
          // Validations for questions that are not tied to undefined as empty value
          const allNonEmptyAnswers =
            form.complete &&
            Object.entries(form.values).every(key => key[1] !== null);

          setIsAllNonEmptyAnswers(allNonEmptyAnswers);

          onTemplateDataChange({
            ...madlibData,
            questionTemplateData: form.values
          });

          setAllQuestionsAnswered(form.complete);
        }}
      />
      {allQuestionsAnswered && !questionConfig?.showFullContentEditor && (
        <>
          <NoticeContentFields newspaper={newspaper} />
          <div className="my-6">
            <Alert
              status="info"
              id="madlib-editor-info"
              icon={<ExclamationCircleIcon className="h-5 w-5" />}
            >
              Please fill out the required information in the blue text fields
              below. You may proceed to the next step once all fields are
              completed.
            </Alert>
          </div>
          <div className="bg-white mb-4 border px-4 py-2 shadow-sm overflow-y-scroll rounded h-128">
            <MadlibEditor
              templateStyles={templateStyles}
              rawTemplate={rawTemplate}
              setRenderedHtml={setRenderedHtml}
              onTemplateDataChange={newTemplateData => {
                // mark that the madlib editor has been initialized and that we can now do validations
                setMadlibEditorInitialized(true);
                onTemplateDataChange(newTemplateData);
              }}
              madlibData={madlibData}
              setValidationResults={setValidationResults}
              handlebarData={noticeHandlebarData}
            />
          </div>
        </>
      )}
    </>
  );
}

export default NoticeEditorMadlib;
